/**
 * Turns an attached file into what the protocol PDF can carry: an image
 * re-encoded as PNG or JPEG (the only two formats pdf-lib embeds), a PDF's own
 * bytes, or a note that it cannot be printed.
 */
import { createPreviewUrl } from "@/domains/documents/repository";
import {
  attachmentPreviewKind,
  browserCanShowImage,
  canTransformImage,
} from "@/domains/pm/components/issues/attachments/attachmentPreview";
import type {
  ProtocolAttachment,
  ProtocolAttachmentContent,
  ProtocolOriginal,
} from "@/domains/pm/utils/protocolAttachments";

/** Long side of a printed image, in pixels: 300 dpi across the A4 content
 *  width is ~2100, so this prints sharp without carrying a 48 MP photo. */
const MAX_IMAGE_PIXELS = 2000;
/** Long enough for the slowest attachment fetch of one export. */
const SIGNED_URL_TTL_SECONDS = 10 * 60;
const JPEG_QUALITY = 0.88;
/** Fetched a few at a time: one at a time stalls on a slow file, and all at
 *  once opens dozens of sockets for an export with many attachments. */
const CONCURRENCY = 3;

function aborted(): DOMException {
  return new DOMException("Protocol attachment load cancelled", "AbortError");
}

async function fetchBlob(url: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Attachment fetch failed: ${response.status}`);
  return response.blob();
}

/**
 * The image's pixels, decoded by the browser and drawn onto a canvas. Every
 * image goes through here, not just the formats pdf-lib cannot read: a phone
 * photo's EXIF rotation is applied by the browser when it draws, and pdf-lib
 * embedding the raw JPEG would print it sideways.
 */
async function rasterise(blob: Blob, keepAlpha: boolean): Promise<ProtocolAttachmentContent> {
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const longSide = Math.max(image.naturalWidth, image.naturalHeight);
    if (longSide === 0) return { type: "failed" };
    const scale = Math.min(1, MAX_IMAGE_PIXELS / longSide);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) return { type: "failed" };
    if (!keepAlpha) {
      // JPEG has no transparency; paper is white.
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const format = keepAlpha ? "png" : "jpg";
    const encoded = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, keepAlpha ? "image/png" : "image/jpeg", JPEG_QUALITY)
    );
    if (!encoded) return { type: "failed" };
    return { type: "image", format, bytes: new Uint8Array(await encoded.arrayBuffer()) };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Drawings, screenshots and logos keep their transparency and crisp edges as
 *  PNG; photographs go to JPEG, which is a tenth of the size. */
function keepsAlpha(filename: string): boolean {
  return /\.(png|gif|svg|webp|bmp|ico)$/i.test(filename);
}

async function storedImageBlob(
  attachment: Extract<ProtocolAttachment, { origin: "stored" }>,
  signal?: AbortSignal
): Promise<Blob | null> {
  const { doc } = attachment;
  // Resized by Storage where it can be — which is also the only way HEIC and
  // TIFF become something a browser can decode — and the original otherwise.
  if (canTransformImage(doc)) {
    try {
      const url = await createPreviewUrl(doc.file_path, {
        width: MAX_IMAGE_PIXELS,
        expiresIn: SIGNED_URL_TTL_SECONDS,
      });
      return await fetchBlob(url, signal);
    } catch (error) {
      if (signal?.aborted) throw aborted();
      if (!browserCanShowImage(doc)) throw error;
    }
  }
  if (!browserCanShowImage(doc)) return null;
  const url = await createPreviewUrl(doc.file_path, { expiresIn: SIGNED_URL_TTL_SECONDS });
  return fetchBlob(url, signal);
}

async function loadImage(
  attachment: ProtocolAttachment,
  signal?: AbortSignal
): Promise<ProtocolAttachmentContent> {
  const filename = attachment.origin === "local" ? attachment.file.name : attachment.doc.filename;
  const blob =
    attachment.origin === "local" ? attachment.file : await storedImageBlob(attachment, signal);
  if (!blob) return { type: "unsupported" };
  return rasterise(blob, keepsAlpha(filename));
}

async function loadPdf(
  attachment: ProtocolAttachment,
  signal?: AbortSignal
): Promise<ProtocolAttachmentContent> {
  const blob =
    attachment.origin === "local"
      ? attachment.file
      : await fetchBlob(
          await createPreviewUrl(attachment.doc.file_path, { expiresIn: SIGNED_URL_TTL_SECONDS }),
          signal
        );
  return { type: "pdf", bytes: new Uint8Array(await blob.arrayBuffer()) };
}

/**
 * One file's content. Never rejects for a broken file — that prints as
 * "could not be included" — only for a cancel, which every caller already
 * treats as one.
 */
export async function loadProtocolAttachment(
  attachment: ProtocolAttachment,
  signal?: AbortSignal
): Promise<ProtocolAttachmentContent> {
  if (signal?.aborted) throw aborted();
  const source =
    attachment.origin === "local"
      ? {
          filename: attachment.file.name,
          content_type: attachment.file.type,
          size: attachment.file.size,
        }
      : attachment.doc;
  const kind = attachmentPreviewKind(source);
  if (!kind) return { type: "unsupported" };
  try {
    return kind === "pdf" ? await loadPdf(attachment, signal) : await loadImage(attachment, signal);
  } catch (error) {
    if (signal?.aborted) throw aborted();
    console.warn("Protocol attachment could not be loaded", error);
    return { type: "failed" };
  }
}

/**
 * Every file's content, keyed by identity, reusing what `cache` already holds
 * — so Preview followed by Export reads each file once. Only successes are
 * cached: a failure is worth retrying on the next press.
 */
export async function loadProtocolAttachments(
  items: ReadonlyArray<{ identity: string; attachment: ProtocolAttachment }>,
  cache: Map<string, ProtocolAttachmentContent>,
  signal?: AbortSignal
): Promise<Map<string, ProtocolAttachmentContent>> {
  const result = new Map<string, ProtocolAttachmentContent>();
  const pending = items.filter(({ identity }) => {
    const cached = cache.get(identity);
    if (cached) result.set(identity, cached);
    return !cached;
  });
  let next = 0;
  const worker = async () => {
    for (let index = next++; index < pending.length; index = next++) {
      const { identity, attachment } = pending[index];
      const content = await loadProtocolAttachment(attachment, signal);
      if (content.type !== "failed") cache.set(identity, content);
      result.set(identity, content);
    }
  };
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker));
  if (signal?.aborted) throw aborted();
  return result;
}

/**
 * The most original bytes one protocol carries. The PDF is the thing people
 * email, and most mail refuses past 25–50 MB; files beyond this stay printed
 * or listed by name, without their original inside.
 */
export const ORIGINALS_BUDGET_BYTES = 50 * 1024 * 1024;

/** A file's size before fetching it, when known. */
function knownSize(attachment: ProtocolAttachment): number | null {
  return attachment.origin === "local" ? attachment.file.size : attachment.doc.size;
}

async function originalBytes(
  attachment: ProtocolAttachment,
  signal?: AbortSignal
): Promise<ProtocolOriginal> {
  if (attachment.origin === "local") {
    return {
      bytes: new Uint8Array(await attachment.file.arrayBuffer()),
      mimeType: attachment.file.type || null,
    };
  }
  const url = await createPreviewUrl(attachment.doc.file_path, {
    expiresIn: SIGNED_URL_TTL_SECONDS,
  });
  const blob = await fetchBlob(url, signal);
  return {
    bytes: new Uint8Array(await blob.arrayBuffer()),
    mimeType: attachment.doc.content_type,
  };
}

/**
 * Each file's original, keyed by identity, within `ORIGINALS_BUDGET_BYTES` in
 * print order. A PDF's bytes are already its original and are not fetched
 * again; a file whose content could not be read is not retried here — it is
 * already reported as not included. `leftOut` counts the files the budget
 * turned away.
 */
export async function loadProtocolOriginals(
  items: ReadonlyArray<{ identity: string; attachment: ProtocolAttachment }>,
  contents: ReadonlyMap<string, ProtocolAttachmentContent>,
  cache: Map<string, ProtocolOriginal>,
  signal?: AbortSignal,
  budget = ORIGINALS_BUDGET_BYTES
): Promise<{ originals: Map<string, ProtocolOriginal>; leftOut: number }> {
  const originals = new Map<string, ProtocolOriginal>();
  let used = 0;
  let leftOut = 0;
  for (const { identity, attachment } of items) {
    if (signal?.aborted) throw aborted();
    const content = contents.get(identity);
    if (!content || content.type === "failed") continue;
    const size = knownSize(attachment);
    if (size != null && used + size > budget) {
      leftOut += 1;
      continue;
    }
    let original = cache.get(identity);
    if (!original) {
      try {
        original =
          content.type === "pdf"
            ? { bytes: content.bytes, mimeType: "application/pdf" }
            : await originalBytes(attachment, signal);
      } catch (error) {
        if (signal?.aborted) throw aborted();
        console.warn("Protocol attachment original could not be loaded", error);
        leftOut += 1;
        continue;
      }
      cache.set(identity, original);
    }
    // Checked again on the real size: a stored file's recorded size can be
    // missing, or stale.
    if (used + original.bytes.byteLength > budget) {
      leftOut += 1;
      continue;
    }
    used += original.bytes.byteLength;
    originals.set(identity, original);
  }
  return { originals, leftOut };
}
